import { useEffect, useState } from "react";
import { ENGINES, engineNow } from "./engines";

// Hive data-flow over the hero film. IN sections fire comet streaks + live data glyphs into the brain (impact ripples at the rim);
// the brain pulses, spins its processing rings and ticker, and fires its decisions OUT (green streams) to the execution section
// and the readout. The Core is located through the film's "cover" crop (film frame 1600x900, Core at 800,420 r≈215).
const ANG = [170, 148, 126, 10, 32, 54];   // 3 left, 3 right, in the lower band under the title and side panel

function layout() {
  const W = window.innerWidth, H = window.innerHeight, k = Math.max(W / 1600, H / 900) * 1.04;
  const x = W / 2, y = H / 2 + (420 - 450) * k, r = 215 * k;
  const rx = Math.min(W * 0.42, Math.max(r * 2.1, W * 0.34)), ry = Math.min(H * 0.3, Math.max(r, 190));
  const spots = ANG.map((a) => [Math.min(W * 0.8, Math.max(W * 0.2, x + Math.cos((a * Math.PI) / 180) * rx)), y + 20 + Math.sin((a * Math.PI) / 180) * ry]);
  return { W, H, core: { x, y, r }, spots };
}

function useLayout() {
  const [l, set] = useState(layout);
  useEffect(() => { const f = () => set(layout()); window.addEventListener("resize", f); return () => window.removeEventListener("resize", f); }, []);
  return l;
}

// spark signals: many tiny white-hot sparks per wire, each its own speed/offset, accelerating into the brain
const SPARKS = Array.from({ length: 9 }, (_, i) => ({ r: 1.9 + ((i * 37) % 10) / 6, dur: 0.75 + ((i * 53) % 10) / 12, at: (i * 0.29) % 1.7, flick: (i * 0.13) % 0.5 }));

// a small electric arc where the signal hits the brain
function arc([x, y], C, seed) {
  const a = Math.atan2(y - C.y, x - C.x), n = 6, pts = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n, j = (((seed * 7 + i * 13) % 9) - 4) * 2.2, ang = a + (t - 0.5) * 0.5;
    pts.push(`${(C.x + Math.cos(ang) * (C.r * 0.86 + j)).toFixed(1)},${(C.y + Math.sin(ang) * (C.r * 0.86 + j)).toFixed(1)}`);
  }
  return pts.join(" ");
}

function wire([x, y], C, out) {
  const a = Math.atan2(y - C.y, x - C.x), ex = C.x + Math.cos(a) * C.r * 0.86, ey = C.y + Math.sin(a) * C.r * 0.86;
  const q = `${(x + ex) / 2},${Math.min(y, ey) - C.r * 0.35}`;
  return { d: out ? `M${ex},${ey} Q${q} ${x},${y}` : `M${x},${y} Q${q} ${ex},${ey}`, hit: out ? [x, y] : [ex, ey] };
}

function Section({ s, at, C, hot, mobile, idx }) {
  const out = s.dir === "out", { d, hit } = wire(at, C, out), [x, y] = at, left = x < C.x;
  const col = out ? "#34D399" : s.color, period = hot ? 1.6 : 4.2;
  return (
    <g className={`cf-agent ${hot ? "is-hot" : ""}`} data-testid={`cinema-flow-${s.id}`}>
      <path d={d} stroke={col} className="cf-wire" />
      {[0, 1].map((k) => (
        <path key={k} d={d} pathLength="1000" stroke={col} className="cf-comet" filter="url(#cf-glow)"
          style={{ animationDuration: `${period}s`, animationDelay: `${k * period / 2 + idx * 0.13}s`, opacity: hot ? 1 : 0.35 }} />
      ))}
      {hot && !mobile && s.data.map((v, k) => (
        <g key={v} className="cf-glyph">
          <rect x="-34" y="-10" width="68" height="18" rx="9" fill="rgba(5,6,13,0.85)" stroke={col} strokeOpacity="0.7" />
          <text y="3" textAnchor="middle" fill={col}>{v}</text>
          <animateMotion dur={`${period * 1.9}s`} begin={`${k * period * 0.63}s`} repeatCount="indefinite" path={d} keyPoints="0.16;1" keyTimes="0;1" calcMode="linear" />
        </g>
      ))}
      {hot && <circle cx={hit[0]} cy={hit[1]} r="6" fill="none" stroke={col} className="cf-ripple" style={{ animationDuration: `${period / 2}s` }} />}
      {SPARKS.slice(0, hot ? SPARKS.length : 3).map((sp, k) => (
        <circle key={`s${k}`} r={sp.r * (hot ? 1 : 0.7)} fill="#fff" className="cf-spark" style={{ "--sc": col, animationDelay: `${sp.flick}s` }}>
          <animateMotion dur={`${(hot ? sp.dur : sp.dur * 2.4).toFixed(2)}s`} begin={`${(sp.at + idx * 0.21).toFixed(2)}s`} repeatCount="indefinite" path={d} keyPoints="0;1" keyTimes="0;1" calcMode="spline" keySplines="0.55 0 0.9 0.6" />
        </circle>
      ))}
      {hot && (
        <polyline points={arc(hit, C, idx)} fill="none" stroke="#fff" strokeWidth="1.4" className="cf-crackle" style={{ "--sc": col }} />
      )}
      <circle cx={x} cy={y} r={hot ? 14 : 9} fill="#05060d" stroke={s.color} strokeWidth="2.5" filter="url(#cf-glow)" />
      <circle cx={x} cy={y} r={4} fill={s.color} className={hot ? "cf-node-beat" : ""} />
      {!mobile && <text x={left ? x - 22 : x + 22} y={y - 6} textAnchor={left ? "end" : "start"} className="cf-name" fill={s.color}>{s.name.toUpperCase()}</text>}
      {!mobile && <text x={left ? x - 22 : x + 22} y={y + 16} textAnchor={left ? "end" : "start"} className="cf-metric">{out ? "◀ " : hot ? "▶ " : ""}{s.metric}</text>}
    </g>
  );
}

function Brain({ C, sub }) {
  const tick = `HIVE · ENGINE · ${sub.toUpperCase()} · CONSENSUS · RISK · EDGE · DECISION · `;
  return (
    <g>
      <path id="cf-tick-path" d={`M${C.x - C.r * 1.3},${C.y} a${C.r * 1.3},${C.r * 1.3} 0 1,1 ${C.r * 2.6},0 a${C.r * 1.3},${C.r * 1.3} 0 1,1 ${-C.r * 2.6},0`} fill="none" />
      <circle className="cf-heart" cx={C.x} cy={C.y} r={C.r * 1.2} fill="url(#cf-core)" />
      <circle className="cf-wave" cx={C.x} cy={C.y} r={C.r} />
      <circle className="cf-wave is-late" cx={C.x} cy={C.y} r={C.r} />
      <circle className="cf-proc" cx={C.x} cy={C.y} r={C.r * 1.1} />
      <circle className="cf-proc is-rev" cx={C.x} cy={C.y} r={C.r * 1.18} />
      <g className="cf-spin" style={{ transformOrigin: `${C.x}px ${C.y}px` }}>
        <text className="cf-tick"><textPath href="#cf-tick-path">{tick.repeat(2)}</textPath></text>
      </g>
      <g className="cf-orbit" style={{ transformOrigin: `${C.x}px ${C.y}px` }}>
        {[0, 72, 144, 216, 288].map((a) => (
          <circle key={a} cx={C.x + Math.cos((a * Math.PI) / 180) * C.r * 1.05} cy={C.y + Math.sin((a * Math.PI) / 180) * C.r * 1.05} r="3" fill="#e9d5ff" filter="url(#cf-glow)" />
        ))}
      </g>
    </g>
  );
}

export function EnginePanel({ engine, stage }) {
  const e = ENGINES[engine], now = engineNow(engine, stage);
  return (
    <div className="cine-panel" data-testid="cinema-engine-panel">
      <div className="cine-mono text-[10px] tracking-[0.3em] text-violet-300/80 uppercase">{e.sub} · illustrative</div>
      <div className="mt-3 space-y-2">
        {e.sections.map((s) => {
          const hot = now.active.includes(s.id);
          return (
            <div key={s.id} className="flex items-center justify-between gap-4 text-sm" style={{ opacity: hot ? 1 : 0.45, transition: "opacity .5s" }}>
              <span className="cine-mono shrink-0 whitespace-nowrap" style={{ color: s.color }}>{s.dir === "out" ? "◀ " : "▶ "}{s.name}</span>
              <span className="text-white/80 text-right">{s.data.join(" · ")}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function HiveFlow({ engine, stage, sc, mobile }) {
  const { W, H, core: C, spots } = useLayout();
  const e = ENGINES[engine], now = engineNow(engine, stage, sc), [verb, line] = now.decision;
  const stop = verb === "PASS", win = /BUY|BOUGHT|FILLED|HOLD|PROFIT|EXIT|SHADOW|STACK/.test(verb);
  const outCol = stop ? "#F87171" : win ? "#34D399" : "#A78BFA";
  const outD = `M${C.x},${C.y + C.r * 0.45} L${C.x},${C.y + C.r * 0.62}`;
  return (
    <svg className="cine-flow" viewBox={`0 0 ${W} ${H}`} data-testid="cinema-hive-flow" aria-hidden="true">
      <defs>
        <radialGradient id="cf-core"><stop offset="0" stopColor="#f5f3ff" stopOpacity="0.9" /><stop offset="0.35" stopColor="#a78bfa" stopOpacity="0.45" /><stop offset="1" stopColor="#38bdf8" stopOpacity="0" /></radialGradient>
        <filter id="cf-glow" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="4" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
      </defs>
      <Brain C={C} sub={e.sub} />
      {e.sections.map((s, i) => <Section key={`${engine}-${s.id}`} s={s} at={spots[i]} C={C} hot={now.active.includes(s.id)} mobile={mobile} idx={i} />)}
      <path d={outD} pathLength="1000" stroke={outCol} className="cf-comet is-out" filter="url(#cf-glow)" />
      <g key={engine + verb + line} className="cf-decision" transform={`translate(${C.x}, ${C.y + C.r * 0.72})`} data-testid="cinema-flow-decision">
        <circle r="10" fill="none" stroke={outCol} className="cf-burst" />
        <rect x="-160" y="-26" width="320" height="54" rx="10" fill="rgba(5,6,13,0.8)" stroke={outCol} strokeOpacity="0.85" />
        <text y="-3" textAnchor="middle" className="cf-verb" fill={outCol}>{verb}</text>
        <text y="18" textAnchor="middle" className="cf-line">{line}</text>
      </g>
    </svg>
  );
}
